import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import './reports.css';

const CreditNotes = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchCreditNotes();
    }
  }, [dateRange, token, i18n.language]);

  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/credit_notes_report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }),
      });

      const result = await response.json();
      if (result.status) {
        setCreditNotes(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch credit notes');
      }
    } catch (error) {
      setError('Error fetching credit notes');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  const calculateTotals = () => {
    const totals = creditNotes.reduce((acc, note) => {
      acc.totalCreditNotes += 1;
      acc.totalRefundAmount += parseFloat(note.grand_total) || 0;
      acc.totalVAT += parseFloat(note.vat) || 0;
      acc.totalSubtotal += parseFloat(note.subtotal) || 0;
      return acc;
    }, {
      totalCreditNotes: 0,
      totalRefundAmount: 0,
      totalVAT: 0,
      totalSubtotal: 0
    });

    return totals;
  };

  if (loading) {
    return (
      <div className="container tables bg-white mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="container tables bg-white mt-5">
      <div className="tableTitle">
        <h3>{t('reports.creditNotes.title')}</h3>
      </div>

      <div className="report-filters">
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">{t('reports.startDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('reports.endDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {creditNotes.length > 0 && (
        <div className="report-summary">
          <div className="row">
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalCreditNotes')}</h5>
                <span className="summary-number">{totals.totalCreditNotes}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalRefundAmount')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalRefundAmount)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalVAT')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalVAT)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalSubtotal')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalSubtotal)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>{t('reports.creditNoteNumber')}</th>
              <th>{t('reports.creditNoteDate')}</th>
              <th>{t('reports.originalInvoice')}</th>
              <th>{t('reports.customerName')}</th>
              <th>{t('reports.reason')}</th>
              <th>{t('reports.subtotal')}</th>
              <th>{t('reports.vat')}</th>
              <th>{t('reports.refundAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {creditNotes.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  {t('reports.noCreditNotes')}
                </td>
              </tr>
            ) : (
              creditNotes.map((note) => (
                <tr key={note.id}>
                  <td>{note.invoice_number}</td>
                  <td>{formatDate(note.invoice_date)}</td>
                  <td>{note.original_invoice_number}</td>
                  <td>{note.customer_name}</td>
                  <td>{note.credit_note_reason}</td>
                  <td>{formatCurrency(note.subtotal)}</td>
                  <td>{formatCurrency(note.vat)}</td>
                  <td>{formatCurrency(note.grand_total)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CreditNotes;
